/* Chiki Toonz motion layer (GSAP 3 + ScrollTrigger + SplitText).
   Progressive enhancement: the site works fully without it, and it does nothing
   when the visitor prefers reduced motion. */
(() => {
  if (!window.gsap || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const { gsap } = window;
  const plugins = [window.ScrollTrigger, window.SplitText].filter(Boolean);
  gsap.registerPlugin(...plugins);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const hasST = !!window.ScrollTrigger;
  const hasSplit = !!window.SplitText;
  const fine = matchMedia('(pointer: fine)').matches;
  document.documentElement.classList.add('ck-motion');

  /* ---------- 1. Hero headline: letters bounce in, brand-color pop ---------- */
  const heroH1 = document.querySelector('.ck-dio-title, .ck-hero h1, .ck-page-hero h1');
  if (heroH1 && hasSplit) {
    document.fonts?.ready.then(() => {
      const split = new SplitText(heroH1, { type: 'words,chars', wordsClass: 'ck-w', charsClass: 'ck-ch' });
      gsap.from(split.chars, {
        yPercent: 120, rotate: () => gsap.utils.random(-25, 25), scale: 0.4, opacity: 0,
        duration: 0.9, ease: 'back.out(2.2)', stagger: { each: 0.035, from: 'start' }, delay: 0.15,
      });
      if (fine) {
        split.chars.forEach((ch) => ch.addEventListener('mouseenter', () => {
          gsap.fromTo(ch, { y: 0 }, { y: -14, rotate: gsap.utils.random(-12, 12), duration: 0.18, yoyo: true, repeat: 1, ease: 'power2.out' });
        }));
      }
    });
  }
  gsap.from('.ck-hero .ck-eyebrow, .ck-hero-lede, .ck-hero .ck-hero-ctas > *', { y: 24, opacity: 0, duration: 0.7, ease: 'power3.out', stagger: 0.08, delay: 0.5 });
  gsap.from('.ck-reel', { y: 80, rotate: (i) => (i ? 18 : -14), opacity: 0, duration: 1.1, ease: 'back.out(1.6)', stagger: 0.15, delay: 0.25, clearProps: 'transform' });

  /* ---------- 2. Hero depth: mouse parallax + scroll parallax ---------- */
  const hero = document.querySelector('.ck-hero');
  if (hero) {
    const layers = [
      { el: hero.querySelector('.ck-hero-bgimg img'), depth: 10 },
      { el: hero.querySelector('.ck-reel--a'), depth: 18 },
      { el: hero.querySelector('.ck-reel--b'), depth: 28 },
      { el: hero.querySelector('.ck-mascot--1'), depth: 44 },
      { el: hero.querySelector('.ck-mascot--2'), depth: 36 },
    ].filter((l) => l.el);
    if (fine) {
      const movers = layers.map((l) => ({ x: gsap.quickTo(l.el, 'x', { duration: 0.8, ease: 'power3' }), y: gsap.quickTo(l.el, 'y', { duration: 0.8, ease: 'power3' }), d: l.depth }));
      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width - 0.5;
        const ny = (e.clientY - r.top) / r.height - 0.5;
        movers.forEach((m) => { m.x(-nx * m.d); m.y(-ny * m.d * 0.6); });
      });
      hero.addEventListener('pointerleave', () => movers.forEach((m) => { m.x(0); m.y(0); }));
    }
    if (hasST) {
      layers.forEach((l) => gsap.to(l.el, { yPercent: -l.depth * 0.6, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } }));
    }
  }

  /* ---------- 3. Section headings: words pop on scroll ---------- */
  if (hasST && hasSplit) {
    document.fonts?.ready.then(() => {
      $$('.ck-head h2, .ck-head-row h2, .ck-split-copy h2').forEach((h) => {
        const s = new SplitText(h, { type: 'words', wordsClass: 'ck-w' });
        gsap.from(s.words, {
          y: 40, opacity: 0, rotate: () => gsap.utils.random(-6, 6), duration: 0.7, ease: 'back.out(2)', stagger: 0.06,
          scrollTrigger: { trigger: h, start: 'top 85%', once: true },
        });
      });
    });
  }

  /* ---------- 4. Toonies: squash & stretch "boing" on tap / hover ---------- */
  const boing = (el) => {
    if (el._boing?.isActive()) return;
    el._boing = gsap.timeline()
      .to(el, { scaleX: 1.18, scaleY: 0.82, duration: 0.12, ease: 'power2.out', transformOrigin: '50% 100%' })
      .to(el, { scaleX: 0.9, scaleY: 1.14, y: -26, duration: 0.18, ease: 'power2.out' })
      .to(el, { scaleX: 1, scaleY: 1, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.35)' });
  };
  $$('.ck-mascot, .ck-peek, .ck-pillar-icon img, .ck-toonie-card .ck-pic img, .ck-orbit img, .ck-sil-opts img, .ck-cc-one img, .ck-star-toon, .ck-star-toonie img').forEach((el) => {
    el.style.pointerEvents = 'auto';
    el.style.cursor = 'pointer';
    el.addEventListener('click', () => boing(el));
    if (fine) el.addEventListener('mouseenter', () => boing(el));
  });

  /* ---------- 5. Stats count up ---------- */
  if (hasST) {
    $$('[data-count]').forEach((el) => {
      const raw = el.textContent.trim();
      const m = raw.match(/[\d.,]+/);
      if (!m) return;
      const numStr = m[0];
      const decimals = /[.,]\d{1,2}$/.test(numStr) && !/[.,]\d{3}/.test(numStr) ? 1 : 0;
      const sep = numStr.includes('.') && /\.\d{3}/.test(numStr) ? '.' : ',';
      const target = parseFloat(numStr.replace(/[.,](?=\d{3}\b)/g, '').replace(',', '.'));
      if (!isFinite(target)) return;
      const fmt = (v) => {
        const s = decimals ? v.toFixed(1).replace('.', raw.includes(',') ? ',' : '.') : Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, sep);
        return raw.replace(numStr, s);
      };
      const obj = { v: 0 };
      el.textContent = fmt(0);
      gsap.to(obj, { v: target, duration: 1.8, ease: 'power2.out', onUpdate: () => (el.textContent = fmt(obj.v)), onComplete: () => (el.textContent = raw),
        scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
    });
  }

  /* ---------- 6. Magnetic primary buttons (desktop) ---------- */
  if (fine) {
    $$('.ck-hero-ctas .ck-btn, .ck-promo').forEach((b) => {
      const mx = gsap.quickTo(b, 'x', { duration: 0.4, ease: 'power3' });
      const my = gsap.quickTo(b, 'y', { duration: 0.4, ease: 'power3' });
      b.addEventListener('pointermove', (e) => {
        const r = b.getBoundingClientRect();
        mx((e.clientX - r.left - r.width / 2) * 0.18);
        my((e.clientY - r.top - r.height / 2) * 0.25);
      });
      b.addEventListener('pointerleave', () => { mx(0); my(0); });
    });
  }

  /* ---------- 7. Cards: gentle 3D tilt on hover (desktop) ---------- */
  if (fine) {
    $$('.ck-char, .ck-card, .ck-hub-featured').forEach((c) => {
      const rx = gsap.quickTo(c, 'rotationX', { duration: 0.5, ease: 'power3' });
      const ry = gsap.quickTo(c, 'rotationY', { duration: 0.5, ease: 'power3' });
      gsap.set(c, { transformPerspective: 900 });
      c.addEventListener('pointermove', (e) => {
        const r = c.getBoundingClientRect();
        ry(((e.clientX - r.left) / r.width - 0.5) * 8);
        rx(-((e.clientY - r.top) / r.height - 0.5) * 8);
      });
      c.addEventListener('pointerleave', () => { rx(0); ry(0); });
    });
  }


  /* ---------- 8. Chiki Town diorama ---------- */
  const dio = document.querySelector('[data-dio]');
  if (dio) {
    const world = dio.querySelector('.ck-dio-world');
    const depthEls = [...dio.querySelectorAll('[data-depth]')];
    const toonies = [...dio.querySelectorAll('.ck-dio-toonie')];
    const motionBtn = dio.querySelector('.ck-dio-motion');
    const tvVideo = dio.querySelector('.ck-dio-screen video');
    let paused = false;

    // Intro: copy, TV pop, Tooniez drop in with squash & stretch
    const intro = gsap.timeline({ delay: 0.2 });
    intro.fromTo('.ck-dio .ck-hero-ctas > *', { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out', stagger: 0.08 }, 0.5)
      .from('.ck-dio-tv', { scale: 0, rotate: -20, duration: 0.8, ease: 'back.out(2)' }, 0.7);
    toonies.forEach((el, i) => {
      const t = 1.1 + i * 0.12;
      intro.fromTo(el, { opacity: 0, y: -160, scaleX: 0.82, scaleY: 1.16 }, { opacity: 1, y: 0, scaleX: 1.2, scaleY: 0.78, duration: 0.36, ease: 'power2.in' }, t)
        .to(el, { y: -18, scaleX: 0.92, scaleY: 1.08, duration: 0.19, ease: 'power2.out' }, t + 0.36)
        .to(el, { y: 0, scaleX: 1, scaleY: 1, duration: 0.24, ease: 'power2.inOut' }, t + 0.55);
    });
    // Idle: Tooniez breathe, each on its own rhythm
    const idle = toonies.map((el, i) => gsap.to(el, { scaleY: 1.04, scaleX: 0.97, duration: 1.1 + i * 0.13, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 2.2 + i * 0.1 }));
    toonies.forEach((el) => el.addEventListener('click', () => boing(el)));

    // Pointer parallax (desktop)
    const movers = depthEls.map((el) => ({ x: gsap.quickTo(el, 'x', { duration: 0.9, ease: 'power3' }), y: gsap.quickTo(el, 'y', { duration: 0.9, ease: 'power3' }), d: +el.dataset.depth }));
    if (fine) {
      dio.addEventListener('pointermove', (e) => {
        if (paused) return;
        const nx = e.clientX / innerWidth - 0.5;
        const ny = e.clientY / innerHeight - 0.5;
        movers.forEach((m) => { m.x(nx * m.d * 2.2); m.y(ny * m.d * 1.4); });
      });
      dio.addEventListener('pointerleave', () => movers.forEach((m) => { m.x(0); m.y(0); }));
    }

    // Scroll: fly into Chiki Town (world zooms, layers separate, copy lifts away)
    let scrollTl = null;
    if (hasST && matchMedia('(min-width: 761px)').matches) {
      scrollTl = gsap.timeline({ scrollTrigger: { trigger: dio, start: 'top top', end: 'bottom bottom', scrub: 0.6 } });
      scrollTl.to(world, { scale: 1.12, ease: 'none' }, 0)
        .to('.ck-dio-copy', { y: -120, opacity: 0, ease: 'none' }, 0);
      depthEls.forEach((el) => scrollTl.to(el, { yPercent: -(+el.dataset.depth) * 0.45, ease: 'none' }, 0));
    }

    // TV loop: load only when the hero is on screen and motion is on
    // The TV loop waits for the page to finish loading; phones get the tiny file; Save-Data gets only the poster
    const tvLite = matchMedia('(max-width: 760px)').matches;
    let tvReady = document.readyState === 'complete';
    if (!tvReady) addEventListener('load', () => { tvReady = true; playTv(); }, { once: true });
    const playTv = () => {
      if (!tvVideo || paused || !tvReady || navigator.connection?.saveData) return;
      if (!tvVideo.src) tvVideo.src = (tvLite && tvVideo.dataset.srcSm) || tvVideo.dataset.src;
      tvVideo.play().catch(() => {});
    };
    if (tvVideo) {
      new IntersectionObserver(([en]) => (en.isIntersecting ? playTv() : tvVideo.pause()), { threshold: 0.2 }).observe(dio);
    }

    // Accessible motion toggle
    motionBtn?.addEventListener('click', () => {
      paused = !paused;
      motionBtn.setAttribute('aria-pressed', String(paused));
      motionBtn.setAttribute('aria-label', paused ? motionBtn.dataset.labelPlay : motionBtn.dataset.labelPause);
      idle.forEach((t) => (paused ? t.pause() : t.resume()));
      if (paused) { movers.forEach((m) => { m.x(0); m.y(0); }); tvVideo?.pause(); intro.progress(1); }
      else playTv();
      if (scrollTl) scrollTl.scrollTrigger[paused ? 'disable' : 'enable']();
    });
  }


  /* ---------- 9. Chiki Town journey: vertical scroll drives the road trip sideways ---------- */
  const journey = document.querySelector('[data-journey]');
  if (journey && hasST) {
    const mm = gsap.matchMedia();
    mm.add('(min-width: 761px)', () => {
      const track = journey.querySelector('[data-jr-track]');
      const bus = journey.querySelector('[data-jr-bus]');
      const wheels = $$('.ck-jr-wheel', journey);
      const header = document.querySelector('.ck-site-header');
      journey.style.setProperty('--jr-hdr', `${header ? header.offsetHeight : 0}px`);
      journey.classList.add('ck-jr--pinned');
      track.scrollLeft = 0;
      const distance = () => Math.max(0, track.scrollWidth - window.innerWidth);
      const tween = gsap.to(track, {
        x: () => -distance(),
        ease: 'none',
        scrollTrigger: {
          trigger: journey,
          start: () => `top top+=${header ? header.offsetHeight : 0}`,
          end: () => `+=${distance()}`,
          pin: true,
          scrub: 0.6,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const px = self.progress * distance();
            journey.style.setProperty('--jr-road', `${-px}px`);
            const turn = (px / (2 * Math.PI * 17)) * 360; // wheel radius 17 in the SVG
            wheels.forEach((w) => { w.style.transform = `rotate(${turn}deg)`; });
            bus.style.translate = `0 ${Math.sin(px / 40) * 2}px`; // a little road bump
          },
        },
      });
      // Each stop's sticker pops in as its district rolls into view
      $$('.ck-jr-stop', journey).forEach((stop) => {
        const stk = stop.querySelector('.ck-jr-stk');
        const card = stop.querySelector('.ck-jr-card');
        gsap.from([stk, card].filter(Boolean), {
          scale: 0.4, opacity: 0, y: 30, rotate: -8, stagger: 0.12, duration: 0.6, ease: 'back.out(2)',
          scrollTrigger: { trigger: stop, containerAnimation: tween, start: 'left 78%', toggleActions: 'play none none reverse' },
        });
      });
      return () => { journey.classList.remove('ck-jr--pinned'); gsap.set(track, { clearProps: 'x' }); };
    });
  }

  /* ---------- Chapter 9:00 · ¡Buenos días!: the Chikis video opens through a clock face ---------- */
  const loopFrame = document.querySelector('.ck-loop-frame');
  if (loopFrame && hasST) {
    const face = loopFrame.querySelector('.ck-loop-clock');
    const tl = gsap.timeline({ scrollTrigger: { trigger: loopFrame, start: 'top 92%', end: 'top 28%', scrub: 0.6 } });
    tl.fromTo(loopFrame, { clipPath: 'circle(12% at 50% 50%)' }, { clipPath: 'circle(75% at 50% 50%)', ease: 'power1.in', duration: 1 }, 0);
    if (face) {
      tl.fromTo(face, { opacity: 1, scale: 1 }, { opacity: 0, scale: 1.6, ease: 'power1.in', duration: 0.45 }, 0.25)
        .fromTo(face.querySelector('.h'), { rotation: 210, svgOrigin: '0 0' }, { rotation: 270, svgOrigin: '0 0', ease: 'none', duration: 0.7 }, 0)
        .fromTo(face.querySelector('.m'), { rotation: 0, svgOrigin: '0 0' }, { rotation: 720, svgOrigin: '0 0', ease: 'none', duration: 0.7 }, 0);
    }
  }

  /* ---------- 10:30 Karaoke: scrolling sings the lyric, word by word; stickers pop as their line finishes ---------- */
  const kk = document.querySelector('[data-ck-karaoke]');
  if (kk && hasST) {
    const card = kk.querySelector('.ck-kk-card');
    const lines = [...kk.querySelectorAll('.ck-kk-line')];
    const words = lines.flatMap((ln) => [...ln.querySelectorAll('.w')].map((w) => ({ w, ln })));
    if (card && words.length) {
      kk.classList.add('ck-kk--live');
      let shown = -1;
      const paint = (n) => {
        if (n === shown) return;
        shown = n;
        words.forEach(({ w }, i) => { w.classList.toggle('on', i < n); w.classList.toggle('now', i === n - 1); });
        lines.forEach((ln) => {
          const ws = words.filter((x) => x.ln === ln);
          const done = ws.length && ws.every(({ w }) => w.classList.contains('on'));
          const started = ws.some(({ w }) => w.classList.contains('on'));
          ln.classList.toggle('is-sung', done);
          ln.classList.toggle('is-current', started && !done);
        });
      };
      ScrollTrigger.create({
        trigger: card, start: 'top 72%', end: 'bottom 40%', scrub: true,
        onUpdate: (self) => paint(Math.round(self.progress * words.length)),
        onLeave: () => paint(words.length),
      });
    }
  }

  if (hasST) window.addEventListener('load', () => ScrollTrigger.refresh());
})();
